/**
 * Разбор культур — платформенная очередь (reference.crop_import_queue).
 *
 * Замыкает контур, который до этого экрана обрывался: тенант жал «эскалировать»
 * на «Культурах хозяйства», термин ложился в очередь — и там оставался навсегда,
 * потому что смотреть на неё было негде. Теперь владелец справочника видит
 * рабочий лист и закрывает термин одним из трёх исходов:
 *
 *   • «Привязать»       → alias на существующий канон (тот же термин у всех
 *                          остальных хозяйств сматчится сам);
 *   • «Завести канон»   → новая культура в справочнике (исход «это другая
 *                          культура», reason=new_crop) + alias;
 *   • «Убрать»          → мусорная строка снимается с очереди.
 *
 * Плюс «Перепроверить» — прогон резолвера по всей очереди: термины, для которых
 * канон/алиас завели уже после постановки, закрываются автоматически.
 *
 * Fuzzy-подсказки показываются как ПОДСКАЗКИ (score в процентах), кликом
 * подставляются в диалог привязки — но не применяются сами. Цена ошибки высока:
 * привязали не тот канон — и он молча разъедется по всем тенантам через alias.
 */
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  EntityPicker,
  type EntityOption,
} from '@/pages/reference/pesticides/EntityPicker';
import { toast } from '@/lib/toast';
import {
  useCreateCropFromQueue,
  useCropQueue,
  useDeleteQueueItem,
  useMapQueueItem,
  useRematchQueue,
} from './queries';
import type {
  CropQueueItem,
  CropQueueQuery,
  EscalationReason,
  QueueStatus,
} from './types';

const CROP_SOURCE = { path: '/api/v1/reference/crops', translated: true };

const STATUS_TABS: { key: QueueStatus; label: string }[] = [
  { key: 'pending', label: 'На разборе' },
  { key: 'mapped', label: 'Привязаны' },
  { key: 'created', label: 'Заведены' },
];

const REASON_FILTERS: { key: 'new_crop' | 'other' | undefined; label: string }[] = [
  { key: undefined, label: 'Все причины' },
  { key: 'new_crop', label: 'Нужен новый канон' },
  { key: 'other', label: 'Прочее' },
];

function reasonBadge(reason: EscalationReason) {
  if (reason === 'new_crop') {
    return (
      <Badge variant="outline" className="border-accent-brand text-accent-brand">
        новый канон
      </Badge>
    );
  }
  if (reason === 'other') return <Badge variant="outline">прочее</Badge>;
  // Легаси-строки (промах ETL до миграции 108) причины не имеют — это не ошибка,
  // просто термин приехал не из кнопки тенанта.
  return <span className="text-xs text-muted-foreground">из импорта</span>;
}

/** Источник термина человеческим языком: кто именно его не смог разобрать. */
function originLabel(item: CropQueueItem): string {
  if (item.tenant_name) return item.tenant_name;
  if (item.tenant_id) return 'хозяйство (без названия)';
  return 'системный импорт';
}

// ─── диалог привязки к существующему канону ───────────────────────────────

function MapDialog({
  item,
  preselect,
  onClose,
}: {
  item: CropQueueItem | null;
  preselect: EntityOption | null;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<EntityOption | null>(preselect);
  const mapMut = useMapQueueItem();

  // Диалог монтируется заново на каждое открытие (key по item.id), поэтому
  // preselect как начальное состояние — корректно, без useEffect-синка.

  const submit = async () => {
    if (!item || !choice) return;
    try {
      await mapMut.mutateAsync({
        id: item.id,
        payload: { crop_id: choice.id, create_alias: true },
      });
      toast.success(`«${item.incoming_name}» → «${choice.name}»`);
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Привязать к культуре</DialogTitle>
          <DialogDescription>
            Термин «{item?.incoming_name}» станет синонимом выбранной культуры.
            Синоним общий для всех хозяйств — следующий импорт и разбор в
            хозяйствах сработают автоматически.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Культура справочника</Label>
          <EntityPicker
            source={CROP_SOURCE}
            value={choice}
            onChange={setChoice}
            placeholder="Найдите культуру…"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!choice || mapMut.isPending}>
            {mapMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Привязать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── диалог «завести новый канон» ─────────────────────────────────────────

function CreateCropDialog({
  item,
  onClose,
}: {
  item: CropQueueItem | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(item?.incoming_name ?? '');
  const [code, setCode] = useState('');
  const createMut = useCreateCropFromQueue();

  const submit = async () => {
    if (!item) return;
    try {
      await createMut.mutateAsync({
        id: item.id,
        payload: { name_ru: name.trim(), code: code.trim() || undefined },
      });
      toast.success(`Культура «${name.trim()}» заведена`);
      onClose();
    } catch (err) {
      // 409 анти-дубль-гейта — не «ошибка сервера», а подсказка «жми привязать».
      toast.error((err as Error).message);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Завести культуру в справочнике</DialogTitle>
          <DialogDescription>
            Создаётся новая товарная культура. Остальные атрибуты (вегетация,
            сроки сева, NPK) дозаполняются потом в редакторе культур.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label htmlFor="crop-name">Название (ru)</Label>
            <Input
              id="crop-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="crop-code">Код (необязательно)</Label>
            <Input
              id="crop-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="durum_winter_wheat"
            />
          </div>
          {item && item.escalation_note && (
            <p className="text-xs text-muted-foreground">
              Комментарий хозяйства: {item.escalation_note}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!name.trim() || createMut.isPending}>
            {createMut.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── страница ─────────────────────────────────────────────────────────────

export function PlatformAdminCropQueuePage() {
  const [status, setStatus] = useState<QueueStatus>('pending');
  const [reason, setReason] = useState<'new_crop' | 'other' | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [mapTarget, setMapTarget] = useState<{
    item: CropQueueItem;
    preselect: EntityOption | null;
  } | null>(null);
  const [createTarget, setCreateTarget] = useState<CropQueueItem | null>(null);

  const query: CropQueueQuery = useMemo(
    () => ({ status, reason, search: search || undefined, limit: 200 }),
    [status, reason, search],
  );
  const q = useCropQueue(query);
  const rematchMut = useRematchQueue();
  const deleteMut = useDeleteQueueItem();

  const items = q.data?.items ?? [];
  const summary = q.data?.summary;

  const runRematch = async () => {
    try {
      const res = await rematchMut.mutateAsync();
      toast.success(
        res.resolved > 0
          ? `Закрыто автоматически: ${res.resolved} из ${res.scanned}`
          : `Проверено ${res.scanned} — новых совпадений нет`,
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const remove = async (item: CropQueueItem) => {
    try {
      await deleteMut.mutateAsync(item.id);
      toast.success(`«${item.incoming_name}» снят с очереди`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <PlatformAdminLayout>
      <div className="space-y-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              Разбор культур
            </h1>
            <p className="text-sm text-muted-foreground">
              Термины, которые система не смогла привязать к справочнику —
              эскалации хозяйств и промахи импорта.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={runRematch}
            disabled={rematchMut.isPending}
          >
            {rematchMut.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Перепроверить
          </Button>
        </header>

        {summary && (
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">На разборе: {summary.pending}</Badge>
            <Badge variant="outline">
              из них нужен канон: {summary.pending_new_crop}
            </Badge>
            <Badge variant="outline">Привязано: {summary.mapped}</Badge>
            <Badge variant="outline">Заведено: {summary.created}</Badge>
          </div>
        )}

        <Card className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1">
              {STATUS_TABS.map((t) => (
                <Button
                  key={t.key}
                  size="sm"
                  variant={status === t.key ? 'default' : 'outline'}
                  onClick={() => setStatus(t.key)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
            {status === 'pending' && (
              <div className="flex flex-wrap gap-1">
                {REASON_FILTERS.map((f) => (
                  <Button
                    key={f.label}
                    size="sm"
                    variant={reason === f.key ? 'secondary' : 'ghost'}
                    onClick={() => setReason(f.key)}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
            )}
            <form
              className="relative ml-auto min-w-[200px] flex-1 sm:max-w-xs"
              onSubmit={(e) => {
                e.preventDefault();
                setSearch(searchInput.trim());
              }}
            >
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={() => setSearch(searchInput.trim())}
                placeholder="Поиск по термину…"
                className="pl-8"
              />
            </form>
          </div>
        </Card>

        <Card className="overflow-hidden">
          {q.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-8 text-center">
              <Check className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {status === 'pending'
                  ? 'Очередь пуста — все термины разобраны.'
                  : 'Здесь пока пусто.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Термин</TableHead>
                  <TableHead className="w-[120px]">Причина</TableHead>
                  <TableHead className="w-[180px]">Откуда</TableHead>
                  <TableHead className="w-[80px] text-right">Вхождений</TableHead>
                  <TableHead>
                    {status === 'pending' ? 'Похожие культуры' : 'Результат'}
                  </TableHead>
                  <TableHead className="w-[220px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">
                      {item.incoming_name}
                      {item.escalation_note && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {item.escalation_note}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>{reasonBadge(item.escalation_reason)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {originLabel(item)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {item.occurrence_count}
                    </TableCell>
                    <TableCell>
                      {item.status === 'pending' ? (
                        item.suggestions.length === 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            похожих нет
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {item.suggestions.map((s) => (
                              <Button
                                key={s.crop_id}
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs"
                                onClick={() =>
                                  setMapTarget({
                                    item,
                                    preselect: { id: s.crop_id, name: s.name },
                                  })
                                }
                              >
                                {s.name}
                                <span className="ml-1 text-muted-foreground">
                                  {Math.round(s.score * 100)}%
                                </span>
                              </Button>
                            ))}
                          </div>
                        )
                      ) : (
                        <span className="text-sm">
                          {item.resolved_crop_name ?? '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.status === 'pending' && (
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setMapTarget({ item, preselect: null })}
                          >
                            <Link2 className="mr-1 h-3.5 w-3.5" />
                            Привязать
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCreateTarget(item)}
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Завести
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Убрать «${item.incoming_name}» из очереди`}
                            onClick={() => remove(item)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <p className="text-[11px] text-muted-foreground">
          Привязка пишет синоним в общий справочник: тот же термин у любого
          другого хозяйства сматчится автоматически при следующем разборе. Сами
          термины хозяйств отсюда не переписываются — решение применить синоним
          остаётся за хозяйством.
        </p>
      </div>

      {mapTarget && (
        <MapDialog
          key={mapTarget.item.id}
          item={mapTarget.item}
          preselect={mapTarget.preselect}
          onClose={() => setMapTarget(null)}
        />
      )}
      {createTarget && (
        <CreateCropDialog
          key={createTarget.id}
          item={createTarget}
          onClose={() => setCreateTarget(null)}
        />
      )}
    </PlatformAdminLayout>
  );
}

export default PlatformAdminCropQueuePage;
